import styled from "styled-components";
import { useEffect, useRef } from "react";
import { CalendarDays, ChevronRight, ChevronLeft, Check } from "lucide-react";

import { theme } from "./theme";
import { wibTodayIso, shiftIso, formatDate } from "./dateFormat";
import { useT } from "./i18n";

const Wrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const DateButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 160px;
  justify-content: center;
  padding: 0.4rem 0.8rem;
  border: 1px solid ${(props) => (props.$off ? theme.goldDark : theme.border)};
  border-radius: 8px;
  background: ${(props) => (props.$off ? theme.goldSoft : "#ffffff")};
  font-size: 0.9rem;
  font-weight: 600;
  color: ${theme.ink};
  cursor: pointer;
  &:hover {
    border-color: ${theme.gold};
  }
`;

// Present (so showPicker works) but invisible and non-interactive; the button drives it.
const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

// Two deliberate looks so the FO can tell at a glance whether the filter sits on today:
//  - $today (disabled): calm/neutral pill, non-interactive — "you are already on today".
//  - not today: solid-gold call-to-action — pops, invites a click to jump back to today.
const TodayButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.75rem;
  border: 1px solid ${(props) => (props.$today ? theme.border : theme.goldDark)};
  border-radius: 8px;
  background: ${(props) => (props.$today ? "#ffffff" : theme.gold)};
  font-size: 0.82rem;
  font-weight: ${(props) => (props.$today ? 600 : 800)};
  color: ${(props) => (props.$today ? theme.muted : theme.ink)};
  cursor: ${(props) => (props.$today ? "default" : "pointer")};
  &:hover {
    background: ${(props) => (props.$today ? "#ffffff" : "#b8965a")};
  }
`;

const DateNav = (props) => {
  const { t, lang } = useT();
  const inputRef = useRef(null);
  const isToday = props.date === wibTodayIso();
  // Hoisted so the effects below can depend on the two values they actually use rather than on
  // the whole props object, which changes on every render.
  const selectedDate = props.date;
  const onDateChange = props.onChange;

  /* Deliberately UNCONTROLLED, and committed on the native `change` event only.
   *
   * React's onChange maps to the DOM `input` event, and Chrome fires that on a date input while
   * the operator is still moving around inside the picker — so every month arrow committed a
   * page visit, which closed the picker. Jumping four months meant four reloads and four
   * reopenings. `change` fires once, when a date is actually chosen.
   */
  useEffect(() => {
    const el = inputRef.current;
    if (el && el.value !== selectedDate) {
      el.value = selectedDate || "";
    }
  }, [selectedDate]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;

    const commit = () => {
      if (el.value && el.value !== selectedDate) onDateChange(el.value);
    };
    el.addEventListener("change", commit);
    return () => el.removeEventListener("change", commit);
  }, [selectedDate, onDateChange]);

  const openPicker = () => {
    const el = inputRef.current;
    if (el && typeof el.showPicker === "function") {
      el.showPicker();
    } else if (el) {
      el.focus();
    }
  };

  return (
    <Wrap>
      <IconButton
        type="button"
        aria-label="prev"
        onClick={() => props.onChange(shiftIso(props.date, -1))}
      >
        <ChevronLeft size={16} />
      </IconButton>

      <DateButton type="button" $off={!isToday} onClick={openPicker}>
        <CalendarDays size={15} />
        {formatDate(props.date, lang)}
        <HiddenInput ref={inputRef} type="date" defaultValue={props.date} />
      </DateButton>

      <IconButton
        type="button"
        aria-label="next"
        onClick={() => props.onChange(shiftIso(props.date, 1))}
      >
        <ChevronRight size={16} />
      </IconButton>

      <TodayButton
        type="button"
        $today={isToday}
        disabled={isToday}
        aria-current={isToday ? "date" : undefined}
        title={isToday ? t("today_current_hint") : t("today_jump_hint")}
        onClick={() => props.onChange(wibTodayIso())}
      >
        {isToday ? <Check size={14} /> : null}
        {t("today")}
      </TodayButton>
    </Wrap>
  );
};

export default DateNav;
